import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { ThemeProvider } from 'styled-components';
import { withPreview } from 'gatsby-source-prismic'

import Layout from '../components/Layout';
import LayoutPartnerPage from '../components/LayoutPartnerPage';
import PageBody from '../components/PageBody';
import SEO from '../components/SEO';
import theme from '../ui/theme';
import ContainerHeader from '../components/ContainerHeader';

const PageTemplate = ({ data: { prismicPage }, location, pageContext }) => {
  const isPartnerPage = Boolean(prismicPage.data.partner_company);
  const hasHero =
    prismicPage.data.body.findIndex((part) => part.slice_type === 'hero') !==
    -1;

  const LayoutComponent = isPartnerPage ? LayoutPartnerPage : Layout;

  const customTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      ...(prismicPage.data.partner_color_1
        ? { partnerColor1: prismicPage.data.partner_color_1 }
        : {}),
      ...(prismicPage.data.partner_color_2
        ? { partnerColor2: prismicPage.data.partner_color_2 }
        : {}),
      ...(prismicPage.data.partner_color_3
        ? { partnerColor3: prismicPage.data.partner_color_3 }
        : {}),
    },
  };

  return (
    <ThemeProvider theme={customTheme}>
      <LayoutComponent
        selectedLocale={pageContext.lang}
        location={location}
        partnerLogo={prismicPage.data.partner_logo}
        hasHero={hasHero}
      >
        <SEO
          selectedLocale={pageContext.lang}
          location={location}
          specifics={{
            title: prismicPage.data.meta_title,
            description: prismicPage.data.meta_description,
            image: prismicPage.data.share_image.url,
          }}
        />
        {!hasHero ? (
          <ContainerHeader
            py={{
              mobile: isPartnerPage ? 'baseUp4' : 'baseUp6',
              smallTablet: isPartnerPage ? 'baseUp6' : 'baseUp8',
              tablet: isPartnerPage ? 'baseUp6' : 'baseUp10',
            }}
            backgroundColor="grayLight"
          />
        ) : null}
        <PageBody
          content={prismicPage.data.body}
          selectedLocale={pageContext.lang}
        />
      </LayoutComponent>
    </ThemeProvider>
  );
};

PageTemplate.propTypes = {
  data: PropTypes.shape({
    prismicPage: PropTypes.shape({
      data: PropTypes.shape({
        meta_title: PropTypes.string,
        meta_description: PropTypes.string,
        share_image: PropTypes.shape({
          url: PropTypes.string,
        }),
        partner_company: PropTypes.string,
        partner_logo: PropTypes.shape({
          alt: PropTypes.string,
          url: PropTypes.string,
          localFile: PropTypes.shape({
            childImageSharp: PropTypes.shape({}),
          }),
        }),
        partner_color_1: PropTypes.string,
        partner_color_2: PropTypes.string,
        partner_color_3: PropTypes.string,
        body: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  pageContext: PropTypes.shape({ lang: PropTypes.string }).isRequired,
};

export default withPreview(PageTemplate);

export const query = graphql`
  query ($id: String!) {
    prismicPage(id: { eq: $id }) {
      data {
        meta_title
        meta_description
        share_image {
          url
        }
        partner_company
        partner_logo {
          alt
          url
        }
        partner_color_1
        partner_color_2
        partner_color_3
        body {
          ... on PrismicPageBodyHero {
            ...PageHeroFragment
          }
          ... on PrismicPageBodyHeroCarousel {
            ...PageHeroCarouselFragment
          }
          ... on PrismicPageBodyLogoRibbon {
            id
            slice_type
          }
          ... on PrismicPageBodyLogosRow {
            ...LogosRowFragment
          }
          ... on PrismicPageBodySteps {
            ...PageStepsFragment
          }
          ... on PrismicPageBodyCallOut {
            ...PageCallOutFragment
          }
          ... on PrismicPageBodyCaseNews {
            ...PageCaseAndNewsFragment
          }
          ... on PrismicPageBodyStatistics {
            ...PageStatisticsFragment
          }
          ... on PrismicPageBodyText {
            ...PageTextFragment
          }
          ... on PrismicPageBodyTextMedia {
            ...PageTextMediaFragment
          }
          ... on PrismicPageBodyEvents {
            ...PageEventsFragment
          }
          ... on PrismicPageBodyComparisonTable {
            ...PageComparisonTableFragment
          }
          ... on PrismicPageBodyNewsList {
            ...PageNewsListFragment
          }
          ... on PrismicPageBodySubscibeNewsletter {
            ...PageSubscribeNewsletterFragment
          }
          ... on PrismicPageBodyFaqs {
            ...PageFAQsFragment
          }
          ... on PrismicPageBodyForm {
            ...PageFormFragment
          }
          ... on PrismicPageBodyTeaserCards {
            ...PageTeaserCardsFragment
          }
          ... on PrismicPageBodyUspCarousel {
            ...PageUSPCarouselFragment
          }
          ... on PrismicPageBodyTabs {
            ...PageTabsFragment
          }
          ... on PrismicPageBodyFullVideo {
            ...PageFullVideoFragment
          }
          ... on PrismicPageBodyCollaborationPartners {
            ...PageCollaborationPartnersFragment
          }
          ... on PrismicPageBodyImageGrid {
            ...PageImageGridFragment
          }
        }
      }
    }
  }
`;
